import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';
import { HyperDxService } from './hyperdx.service';
import { IntercomService } from './intercom.service';
import { Observable, switchMap } from 'rxjs';
import { ILoginRequest, IUser, IUserPage } from '../models/user.model';
import { IError } from '../models/error.model';
import { GlobalConfigService } from './global-config.service';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private get baseUrl(): string {
    return this.globalConfig.getApiUrl() + '/users';
  }

  constructor(
    private http: HttpClient,
    private globalConfig: GlobalConfigService,
    private authService: AuthService,
    private hyperDxService: HyperDxService,
    private intercomService: IntercomService,
    private contextService: ContextService
  ) { }

  authenticate(body: ILoginRequest): Promise<IUser> {
    return new Promise((resolve, reject) => {
      this.http.post<IUser>(`${this.baseUrl}/login`, body)
        .subscribe({
          next: (user) => {

            this.authService.clear();

            // O backend retorna access_token, mas também pode retornar jwt_token para compatibilidade
            const token = user.access_token || user.jwt_token || '';
            this.authService.setToken(token);
            
            // Salvar refresh_token se disponível
            if (user.refresh_token) {
              this.authService.setRefreshToken(user.refresh_token);
            }
            
            this.authService.setUsername(user.name ? user.name : '');
            this.authService.setUserEmail(user.email ? user.email : '');
            this.authService.setCompanyName(user.company?.corporate_name || '');
            this.authService.setPermission(user.permission ? user.permission : '');
            this.authService.setAnamneseAuthType(user.company?.contracted_anamnese_type || 'NOT_CONTRACTED');
            this.authService.setSendDocsType(user.company?.sign_delivery_type || 'DELIVERY_METHOD_SMS');
            this.authService.setUserId(user.id ? user.id : '');
            this.authService.setModules(user.modules || []);
            this.authService.setUserImage(user.company? user.company.logo : '');
            // Armazenar dados da clínica se disponíveis
            if (user.company?.cnpj) {
              this.authService.setClinicCnpj(user.company.cnpj);
            }
            if (user.company?.id) {
              this.authService.setClinicId(user.company.id);
            }

            // Armazena o JWT do Intercom gerado pelo backend (Identity Verification)
            this.authService.setIntercomJwt(user.intercom_jwt || '');
            this.authService.setPatientModelPermission(user.company?.is_model_patient_active || false);
            this.authService.setTreatmentPlanPermission(user.company?.has_treatment_plans || false);
            this.authService.setCalculatorPermission(user.company?.has_calculator || false);

            // Popula o ContextService com a clínica do login para o switcher mostrar imediatamente
            if (user.company?.cnpj && user.permission !== 'ROOT') {
              this.contextService.setActiveClinic({
                id: user.company.id || '',
                cnpj: user.company.cnpj,
                name: user.company.corporate_name || '',
                modules: (user.modules || []).map((m: any) => ({ id: m.id || '', description: m.description || '' })),
                hasTreatmentPlans: user.company?.has_treatment_plans || false,
                hasCalculator: user.company?.has_calculator || false,
                contractedAnamneseType: user.company?.contracted_anamnese_type || 'NOT_CONTRACTED',
                signDeliveryType: user.company?.sign_delivery_type || 'DELIVERY_METHOD_SMS',
                isModelPatientActive: user.company?.is_model_patient_active || false,
                logo: user.company?.logo || ''
              });
            }

            // Configura a identificação do usuário no HyperDX
            this.hyperDxService.setUserIdentification();

            // Inicializa o Intercom com os dados do usuário (somente em dev)
            this.intercomService.boot();

            // Registra o evento de login
            this.hyperDxService.logAction('user-login', {
              userId: user.id,
              userName: user.name,
              userEmail: user.email,
              permission: user.permission,
              companyName: user.company?.corporate_name
            });

            resolve(user);
          },
          error: (err) => {
            reject(err?.error || err);
          }
        });
    });
  }

  listUsers(pageNumber: number, pageSize: number, orderBy: string, sortBy: string, search: string = ''): Observable<IUserPage | IError> {
    const params: any = {
      pageNumber,
      pageSize,
      orderBy,
      sortBy
    };
    
    if (search) {
      params.search = search;
    }
    
    return this.http.get<any>(`${this.baseUrl}`, { params });
  }

  getUserById(userId: string): Observable<IUser> {
    return this.http.get<IUser>(`${this.baseUrl}/${userId}`);
  }

  registerUser(user: IUser): Observable<IUser> {
    return this.http.post<IUser>(`${this.baseUrl}/register`, user);
  }

  changeStatus(userId: string, status: boolean): Observable<IUser> {
    return this.http.put<IUser>(`${this.baseUrl}/${userId}/status?blocked=${status}`, {});
  }

  deleteUser(userId: string): Observable<IUser> {
    return this.http.delete<IUser>(`${this.baseUrl}/${userId}`);
  }

  updateUser(userId: string, user: IUser): Observable<IUser> {
    return this.http.put<IUser>(`${this.baseUrl}/${userId}`, user);
  }

  /**
   * Atualiza os vínculos de clínicas (acesso multi-clínica) de um usuário.
   * PUT /users/{id}/access
   * Body: { clinics: ["id1", "id2"], default: "idDefault" }
   */
  updateUserAccess(userId: string, clinicIds: string[], defaultClinicId: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/${userId}/access`, {
      clinics: clinicIds,
      default: defaultClinicId
    });
  }

  /**
   * Retorna os vínculos de clínicas de um usuário.
   * GET /users/{id}/access
   */
  getUserAccess(userId: string): Observable<{ clinics: any[]; default: string }> {
    return this.http.get<{ clinics: any[]; default: string }>(`${this.baseUrl}/${userId}/access`);
  }

  /**
   * Reseta a senha de um usuário (admin-initiated).
   * O usuário será obrigado a trocar a senha no próximo login.
   * PUT /users/{id}/reset-password
   */
  resetPassword(userId: string, password: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${userId}/reset-password`, { password });
  }
}
